import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { openIncident } from "@/pages/manufacturing/qcIncidentsApi";
import type { WirelessCheckDevice } from "@/pages/manufacturing/wirelessCheckApi";
import { linkWirelessCheckQcIncident } from "@/pages/manufacturing/wirelessCheckApi";

function buildDefaultIssue(
  device: WirelessCheckDevice,
  attemptNumber: number,
  failureModeName: string | null,
): string {
  if (failureModeName != null && failureModeName.trim() !== "") {
    return `${failureModeName} (check ${attemptNumber}) — ${device.deviceLabelSerial}`;
  }
  return `Wireless check failed (check ${attemptNumber}) — ${device.deviceLabelSerial}`;
}

function buildDefaultNotes(device: WirelessCheckDevice, attemptNumber: number): string {
  const lines = [
    `Wireless check station — attempt ${attemptNumber}.`,
    device.imei != null && device.imei.trim() !== "" ? `IMEI: ${device.imei}` : null,
    device.finishedSku != null ? `SKU: ${device.finishedSku}` : null,
    `Portal check-in since configure: ${device.portal.hasWirelessCheckin ? "yes" : "no"} (${device.portal.checkinCount} check-in(s)).`,
    `Non-zero GPS since configure: ${device.portal.hasValidGps ? "yes" : "no"}.`,
    device.portalLookupError != null ? `Portal lookup: ${device.portalLookupError}` : null,
  ];
  return lines.filter((line) => line != null).join("\n");
}

export function WirelessCheckQcIncidentModal({
  open,
  device,
  attemptNumber,
  failureModeId,
  failureModeName,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  device: WirelessCheckDevice | null;
  attemptNumber: number;
  failureModeId: number | null;
  failureModeName: string | null;
  onClose: () => void;
  onSubmitted: (incidentId: number) => void;
}): JSX.Element | null {
  const [issue, setIssue] = useState("");
  const [issueNotes, setIssueNotes] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || device == null) return;
    setIssue(buildDefaultIssue(device, attemptNumber, failureModeName));
    setIssueNotes(buildDefaultNotes(device, attemptNumber));
    setError(null);
  }, [open, device, attemptNumber, failureModeName]);

  if (!open || device == null) return null;

  const serial = device.deviceLabelSerial;

  const submit = async () => {
    const trimmedIssue = issue.trim();
    if (!trimmedIssue) {
      setError("Enter a short issue summary.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const incident = await openIncident({
        type: "device",
        serial,
        issue: trimmedIssue,
        issue_notes: issueNotes.trim() || null,
        failureModeIds: failureModeId != null ? [failureModeId] : undefined,
      });
      await linkWirelessCheckQcIncident(serial, incident.id);
      onSubmitted(incident.id);
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader>
          <h2 className="text-lg font-semibold">Log QC incident</h2>
          <p className="text-sm text-muted-foreground">
            Wireless check failed on check {attemptNumber}. Submit an incident or ask a manufacturing administrator
            for an override.
            {failureModeName != null && failureModeName.trim() !== ""
              ? ` This incident will be linked to ${failureModeName}.`
              : ""}
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="rounded border bg-muted/30 p-3 text-sm">
            <p>
              <span className="font-medium">Device label:</span> {serial}
            </p>
            {device.imei != null && device.imei.trim() !== "" ? (
              <p>
                <span className="font-medium">IMEI:</span> {device.imei}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="wireless-check-qc-issue">Issue (short summary)</Label>
            <Input
              id="wireless-check-qc-issue"
              value={issue}
              onChange={(event) => setIssue(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="wireless-check-qc-notes">Issue notes</Label>
            <textarea
              id="wireless-check-qc-notes"
              className="mt-1 min-h-[120px] w-full rounded border px-2 py-1 text-sm"
              value={issueNotes}
              onChange={(event) => setIssueNotes(event.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={pending} onClick={() => void submit()}>
              {pending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Submit incident"
              )}
            </Button>
            <Button type="button" variant="secondary" disabled={pending} onClick={onClose}>
              Close
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
